import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { startWith } from 'rxjs';
import {
  ProductionDeviationAssessment,
  ProductionDeviationActionOutcome,
  ProductionDeviationCase,
  ProductionDeviationRole,
  ProductionDeviationStep,
  ProductionDeviationVerdict,
  WorkflowItem,
} from '#models';
import { PersonNameService } from '#services';
import { EngineCaseStore } from '../../engine/engine-case.store';
import {
  ENGINE_ROLE_BY_ROLE,
  engineRoleLabel,
  roleFromEngineRole,
  toProductionDeviationCase,
} from '../../mappers/engine-production-deviation.mapper';
import {
  PRODUCTION_DEVIATION_ACTION_STATUS_LABELS,
  PRODUCTION_DEVIATION_ASSESSMENT_LABELS,
  PRODUCTION_DEVIATION_DEFECT_DISPOSITION,
  PRODUCTION_DEVIATION_OUTCOME_LABELS,
  PRODUCTION_DEVIATION_ROLE_LABELS,
  activeActionItem,
  actionProgressSummary,
  buildActionRows,
  defectOwnerFor,
  queueSummary,
  resolvedItems,
  stepRole,
} from '../../mappers/production-deviation-workflow.mapper';
import { Uc2LifecycleRailComponent } from '../uc2-lifecycle-rail/uc2-lifecycle-rail.component';
import { DecideHttpRequest } from '../../../../../services/workflow-engine/generated/models/decide-http-request';
import { buildAuditTrail } from '../../mappers/audit-trail.mapper';
import { WorkflowAuditTrailComponent } from '../workflow-audit-trail/workflow-audit-trail.component';
import { auditPersonOids, toRailStages } from '../../engine/engine-case.mapper';
import { WorkflowPublishedGraphComponent } from '../workflow-published-graph/workflow-published-graph.component';


@Component({
  selector: 'app-production-deviation-workflow',
  imports: [NgTemplateOutlet, ReactiveFormsModule, Uc2LifecycleRailComponent, WorkflowAuditTrailComponent, WorkflowPublishedGraphComponent],
  providers: [EngineCaseStore],
  templateUrl: './production-deviation-workflow.component.html',
  styleUrl: './production-deviation-workflow.component.scss',
})
export class ProductionDeviationWorkflowComponent {
  item = input.required<WorkflowItem>();
  engineCaseId = input.required<string>();

  readonly store = inject(EngineCaseStore);
  private _names = inject(PersonNameService);

  readonly Step = ProductionDeviationStep;
  readonly Verdict = ProductionDeviationVerdict;
  readonly roleLabels = PRODUCTION_DEVIATION_ROLE_LABELS;
  readonly assessmentLabels = PRODUCTION_DEVIATION_ASSESSMENT_LABELS;
  readonly outcomeLabels = PRODUCTION_DEVIATION_OUTCOME_LABELS;
  readonly actionStatusLabels = PRODUCTION_DEVIATION_ACTION_STATUS_LABELS;
  readonly assessments = Object.values(ProductionDeviationAssessment);

  private _selectedCandidateIds = signal<string[]>([]);
  private _checkedCriteria = signal<string[]>([]);

  confirmForm = new FormGroup({
    verdict: new FormControl<ProductionDeviationVerdict>(ProductionDeviationVerdict.Confirmed, { nonNullable: true }),
    assessment: new FormControl<ProductionDeviationAssessment>(ProductionDeviationAssessment.Undetermined, { nonNullable: true }),
  });
  returnReason = new FormControl('', { nonNullable: true });
  rejectionReason = new FormControl('', { nonNullable: true });
  completionNote = new FormControl('', { nonNullable: true });
  actionReason = new FormControl('', { nonNullable: true });

  private _confirmValue = toSignal(this.confirmForm.valueChanges.pipe(startWith(this.confirmForm.getRawValue())), {
    initialValue: this.confirmForm.getRawValue(),
  });
  private _returnReasonValue = toSignal(this.returnReason.valueChanges.pipe(startWith('')), { initialValue: '' });
  private _rejectionReasonValue = toSignal(this.rejectionReason.valueChanges.pipe(startWith('')), { initialValue: '' });
  private _noteValue = toSignal(this.completionNote.valueChanges.pipe(startWith('')), { initialValue: '' });
  private _actionReasonValue = toSignal(this.actionReason.valueChanges.pipe(startWith('')), { initialValue: '' });
  actionOutcomes = computed<readonly ProductionDeviationActionOutcome[]>(() => {
    const allowed: ProductionDeviationActionOutcome[] = ['done', 'not_applicable', 'cannot_complete'];
    return allowed.filter(outcome => this.store.form()?.outcomes.includes(outcome));
  });
  hasExceptionOutcomes = computed(() => this.actionOutcomes().some(outcome => outcome !== 'done'));
  actionSummary = computed(() => actionProgressSummary(this.workflowCase()));

  workflowCase = computed<ProductionDeviationCase>(() => {
    const composed = this.store.composed();
    if (!composed) {
      throw new Error('The workflow case must be loaded before rendering its details.');
    }
    return toProductionDeviationCase(composed, this.store.form(), this.item(), oid => this._names.nameOf(oid));
  });
  auditTrail = computed(() => {
    const composed = this.store.composed();
    if (!composed) {
      return { days: [] };
    }
    const candidateTitles: Record<string, string> = {};
    for (const candidate of composed.dossier?.candidates ?? []) {
      const title = candidate.explanation['title'];
      candidateTitles[candidate.candidateId] = typeof title === 'string' && title ? title : candidate.candidateId;
    }
    const itemRoles: Record<string, string> = {};
    for (const action of this.workflowCase().actionItems) {
      const role = ENGINE_ROLE_BY_ROLE[action.targetRole];
      if (role) {
        itemRoles[action.id] = role;
      }
    }
    return buildAuditTrail({
      entries: composed.audit.entries,
      nodes: composed.graph.nodes,
      candidateTitles,
      itemRoles,
      nameOf: oid => this._names.nameOf(oid),
    });
  });
  permissions = computed(() => {
    const current = this.workflowCase();
    const canDecide = this.store.canDecide();
    return {
      canClaim: this.store.canClaim(),
      canConfirm: canDecide && current.step === ProductionDeviationStep.ConfirmFindings,
      canApprove: canDecide && current.step === ProductionDeviationStep.ApproveFindings,
      canCompleteAction: canDecide && current.step === ProductionDeviationStep.ExecuteActions,
    };
  });
  /** A command in flight to the engine; the buttons wait for its re-read. */
  busy = computed(() => this.store.busy());
  commandError = computed(() => this.store.commandError());
  stages = computed(() => toRailStages(this.store.graph()));
  actionRows = computed(() => buildActionRows(this.workflowCase()));
  additionalActions = computed(() => {
    const current = this.workflowCase();
    return this.actionRows().filter(row =>
      row.item.candidateId === null || !current.candidates.some(candidate => candidate.candidateId === row.item.candidateId),
    );
  });
  actionsForCandidate(candidateId: string | null) {
    return candidateId === null
      ? this.additionalActions()
      : this.actionRows().filter(row => row.item.candidateId === candidateId);
  }
  /** An unowned later step the viewer can answer directly; their response claims it. */
  respondsWithoutClaim = computed(() =>
    this.store.claimsOnDecide(),
  );
  queue = computed(() => {
    const label = this._queueRoleLabel();
    return this.respondsWithoutClaim() && label
      ? `Waiting in the ${label} queue — open the step to respond`
      : queueSummary(this.workflowCase());
  });
  private _queueRoleLabel = computed(() => {
    const role = this.store.stepRole();
    return role ? engineRoleLabel(role) : null;
  });
  stepLabel = computed(() => this.isClosed() ? 'Closed' : (this.store.form()?.title ?? this.store.graph().find(node => node.status === 'active')?.title ?? 'Open'));
  isClosed = computed(() => this.workflowCase().step === ProductionDeviationStep.Closed);
  referenceStepKey = computed(() => {
    const state = this.store.composed()?.state;
    return `${state?.caseId}|${state?.activeStep?.stepId}|${this.workflowCase().step}`;
  });
  activeItem = computed(() => activeActionItem(this.workflowCase()));
  activeCandidate = computed(() => {
    const candidateId = this.activeItem()?.candidateId;
    return this.workflowCase().candidates.find(entry => entry.candidateId === candidateId) ?? null;
  });
  executionRows = computed(() =>
    this.actionRows().filter(row => row.status !== 'excluded' && row.status !== 'planned' && row.status !== 'awaiting-selection'),
  );
  checkedCriteria = this._checkedCriteria.asReadonly();
  completedCount = computed(() => {
    const current = this.workflowCase();
    return resolvedItems(current).filter(entry => current.completions[entry.id]?.outcome === 'done').length;
  });
  resolvedCount = computed(() => resolvedItems(this.workflowCase()).length);

  graphActions = computed(() => this.executionRows().map(row => ({
    id: row.item.id,
    description: row.item.description,
    role: this.roleLabels[row.item.targetRole],
    status: row.status,
    statusLabel: this.actionStatusLabels[row.status],
  })));

  requiredRole = computed(() => stepRole(this.workflowCase()));

  /** Draft edits stay local; submitting claims an unowned step before sending the decision. */
  canWork = computed(() => this.permissions().canConfirm || this.permissions().canClaim);

  waitingMessage = computed(() => this._engineWaitingMessage(this.workflowCase()));

  selectedCandidateIds = computed(() =>
    this.canWork() ? this._selectedCandidateIds() : this.workflowCase().findings.selectedCandidateIds,
  );

  isRejected = computed(() => this._confirmValue().verdict === ProductionDeviationVerdict.Rejected);
  /**
   * Whether this case's class version captures the rejection reason on Confirm findings. A case pinned to an earlier
   * version refuses that capture (422), so its reason travels as the decision's audited reason instead.
   */
  rejectionCapturesReason = computed(
    () =>
      this.store.form()?.captures.some(
        capture => capture.record === 'findings' && capture.fields.some(field => field.name === 'rejectionReason'),
      ) ?? false,
  );
  confirmBlockedReason = computed<string | null>(() => {
    if (this.isRejected()) {
      return this._rejectionReasonValue().trim().length === 0 ? 'Add a reason for rejecting.' : null;
    }
    return this._selectedCandidateIds().length === 0 ? 'Select at least one candidate explanation to act on.' : null;
  });
  /** The approver's most privileged UC1 role, resolved from the engine's role grants. */
  defectOwner = computed<ProductionDeviationRole | null>(() => {
    const roles = this.store.heldRoleNames().map(roleFromEngineRole).filter((role): role is ProductionDeviationRole => role !== null);
    return defectOwnerFor(roles);
  });
  /**
   * Whether this case's class version captures a defect at approval. ADR 0037 dropped the UC1 defect declaration, so
   * current versions capture nothing there and refuse one (422); a case pinned to an earlier version still needs it.
   */
  approvalCapturesDefect = computed(
    () =>
      this.store.form()?.captures.some(
        capture => capture.record === 'findings' && capture.fields.some(field => field.name === 'defect'),
      ) ?? false,
  );
  canApprove = computed(() => !this.approvalCapturesDefect() || this.defectOwner() !== null);
  confirmLabel = computed(() =>
    this._confirmValue().verdict === ProductionDeviationVerdict.Rejected ? 'Dismiss case' : 'Confirm and send for approval',
  );
  canReturn = computed(() => this._returnReasonValue().trim().length > 0);
  hasActionReason = computed(() => this._actionReasonValue().trim().length > 0);
  private _exceptionAttempted = signal(false);
  exceptionBlockedReason = computed(() =>
    this._exceptionAttempted() && this.hasActionReason() && !this._noteValue().trim()
      ? 'Add a completion note before choosing Not applicable or Cannot complete.'
      : null,
  );
  canComplete = computed(() => !this.hasActionReason() && this._noteValue().trim().length > 0 && this.allCriteriaMet());
  canResolveException = computed(() => this._noteValue().trim().length > 0 && this._actionReasonValue().trim().length > 0);
  allCriteriaMet = computed(() => {
    const active = this.activeItem();
    return !!active && active.completionCriteria.every(criterion => this._checkedCriteria().includes(criterion));
  });
  checkedCount = computed(() => {
    const active = this.activeItem();
    return active ? active.completionCriteria.filter(criterion => this._checkedCriteria().includes(criterion)).length : 0;
  });
  completeBlockedReason = computed<string | null>(() => {
    if (this.hasActionReason()) {
      return 'Choose Not applicable or Cannot complete, or clear Reason to mark the action complete.';
    }
    if (!this.allCriteriaMet()) {
      return 'Confirm every completion criterion to complete this action.';
    }
    return this._noteValue().trim().length === 0 ? 'Add a completion note.' : null;
  });

  /** The engine step id is empty until the case loads, so the form resets once on arrival but not on a claim's re-read. */
  private _resetKey = computed(() => {
    if (!this.store.composed()) return null;
    const current = this.workflowCase();
    const stepId = this.store.composed()?.state.activeStep?.stepId ?? '';
    return `${this.item().id}|${current.step}|${stepId}|${Object.keys(current.completions).length}`;
  });

  constructor() {
    effect(() => {
      const caseId = this.engineCaseId();
      if (caseId) {
        this.store.load(caseId);
      }
    });

    effect(() => {
      const composed = this.store.composed();
      if (!composed) {
        return;
      }
      this._names.request([
        composed.state.ownerOid,
        ...auditPersonOids(composed.audit.entries),
      ]);
    });

    effect(() => {
      if (!this._resetKey()) return;
      untracked(() => {
        this._resetConfirmForm();
        this.returnReason.reset('');
        this.completionNote.reset('');
        this.actionReason.reset('');
        this._exceptionAttempted.set(false);
        this._checkedCriteria.set([]);
      });
    });
  }

  toggleCriterion(criterion: string): void {
    this._checkedCriteria.update(checked =>
      checked.includes(criterion) ? checked.filter(entry => entry !== criterion) : [...checked, criterion],
    );
  }

  toggleCandidate(candidateId: string): void {
    if (!this.canWork() || this.busy()) {
      return;
    }
    this._selectedCandidateIds.update(selected =>
      selected.includes(candidateId) ? selected.filter(id => id !== candidateId) : [...selected, candidateId],
    );
  }

  onConfirm(): void {
    if (!this.canWork() || this.busy() || this.confirmBlockedReason()) {
      return;
    }
    const value = this.confirmForm.getRawValue();
    const confirmed = value.verdict === ProductionDeviationVerdict.Confirmed;
    // A confirmation never carries a reason, whatever the hidden control still holds.
    const reason = confirmed ? '' : this.rejectionReason.getRawValue().trim();
    const asCapture = !confirmed && this.rejectionCapturesReason();
    this.store.decideWith({
      outcome: confirmed ? 'confirmed' : 'dismissed',
      captures: {
        findings: {
          verdict: value.verdict,
          assessment: value.assessment,
          ...(confirmed ? { selectedCandidates: this._selectedCandidateIds() } : {}),
          ...(asCapture ? { rejectionReason: reason } : {}),
        },
      } as unknown as DecideHttpRequest['captures'],
      ...(!confirmed && !asCapture ? { reason } : {}),
    }, { claimStartStep: true });
  }

  onApprove(): void {
    if (!this.approvalCapturesDefect()) {
      this.store.decideWith({ outcome: 'approved' });
      return;
    }
    const defectOwner = this.defectOwner();
    const defectDisposition = PRODUCTION_DEVIATION_DEFECT_DISPOSITION;
    if (!defectOwner) {
      return;
    }
    const owner = ENGINE_ROLE_BY_ROLE[defectOwner];
    if (!owner) {
      return;
    }
    this.store.decideWith({
      outcome: 'approved',
      captures: { findings: { defect: { owner, disposition: defectDisposition } } } as unknown as DecideHttpRequest['captures'],
    });
  }

  onReturn(): void {
    const reason = this.returnReason.getRawValue().trim();
    if (reason) {
      this.store.decideWith({ outcome: 'returned', reason });
    }
  }

  onCompleteAction(outcome: ProductionDeviationActionOutcome = 'done'): void {
    if (!this.permissions().canCompleteAction || this.busy() || !this.actionOutcomes().includes(outcome)) {
      return;
    }
    if (outcome !== 'done') {
      this._exceptionAttempted.set(true);
    }
    if (outcome === 'done' ? !this.canComplete() : !this.canResolveException()) {
      return;
    }
    this.store.decide(outcome, {
      action_completion__completionNote: this.completionNote.getRawValue().trim(),
    }, outcome === 'done' ? '' : this.actionReason.getRawValue().trim());
  }

  private _engineWaitingMessage(current: ProductionDeviationCase): string {
    const role = this.store.stepRole();
    if (this.isClosed() || !role) {
      return '';
    }
    const label = engineRoleLabel(role);
    if (current.owner) {
      return `Owned by ${current.owner} as ${label}.`;
    }
    // A role holder can work an unowned step, so only a viewer without the role sees it waiting.
    return `Waiting in the ${label} queue. You do not hold the ${label} role.`;
  }

  candidateTitle(candidateId: string): string {
    return this.workflowCase().candidates.find(entry => entry.candidateId === candidateId)?.title ?? candidateId;
  }

  /** Confirm findings always opens on the defaults; later steps mirror what the analyst recorded. */
  private _resetConfirmForm(): void {
    const current = this.workflowCase();
    const atConfirm = current.step === ProductionDeviationStep.ConfirmFindings;
    this.confirmForm.reset({
      verdict: atConfirm ? ProductionDeviationVerdict.Confirmed : (current.findings.verdict ?? ProductionDeviationVerdict.Confirmed),
      assessment: atConfirm ? current.proposedAssessment : (current.findings.assessment ?? current.proposedAssessment),
    });
    this.rejectionReason.reset('');
    this._selectedCandidateIds.set(atConfirm ? [] : [...current.findings.selectedCandidateIds]);
  }
}
